import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import ColumnDefinition from '../../pieces/grids/columnGenerator';
import MyPaginatedGrid from '../../pieces/grids/paginatedGridWrapper';
import base_url from '../../baseUrls';
import {
  PARTNER_FIELDS,
  PARTNER_PROFILES,
  PARTNER_STATUSES,
} from '../../constants/formFields/partnerFields';

/**
 * One object for the life of the page, not a fresh literal per render. The paginated grid resets its
 * datasource whenever `fetchParams` changes identity, so an inline literal refetched on every parent
 * render, which can land mid-draw and throw AG Grid's "cannot get grid to draw rows when it is in the
 * middle of drawing rows".
 */
const FETCH_PARAMS = {url: `${base_url.api}partners/all`, respKey: 'partners'};

const PartnersGrid = (props) => {
  const viewController = props.viewController;
  const ls_name = 'partners_grid';

  const getContextMenuItems = (params) => {
    if (!params.node) return [];

    return [
      {
        name: 'Open',
        action: () => props.onOpen(params.node.data),
      },
      'separator',
      {
        name: 'Export Selected (.xlsx)',
        action: () => params.api.exportDataAsExcel({onlySelected: true}),
      },
      'copy',
    ];
  };

  const columnDefs = useMemo(() => {
    return [
      new ColumnDefinition({field: PARTNER_FIELDS.ID, hide: true}),
      new ColumnDefinition({
        field: PARTNER_FIELDS.NAME,
        lockPinned: true,
        pinned: 'left',
        checkboxSelection: true,
        width: 240,
      }),
      new ColumnDefinition({
        field: PARTNER_FIELDS.STATUS,
        width: 130,
        filter: 'agSetColumnFilter',
        filterValues: Object.values(PARTNER_STATUSES).map((s) => s.value),
        valueGetter: ({data}) =>
          PARTNER_STATUSES[data?.status]?.label ?? data?.status,
      }),
      new ColumnDefinition({
        field: PARTNER_FIELDS.PROFILE,
        width: 150,
        // The full label is long; the grid shows the letter plus the word that
        // actually matters when scanning a list.
        valueGetter: ({data}) =>
          data?.profile === PARTNER_PROFILES.A.value
            ? 'A - Full'
            : 'B - Masked',
      }),
      new ColumnDefinition({field: PARTNER_FIELDS.CONTACT_EMAIL, minWidth: 220}),
      new ColumnDefinition({
        field: PARTNER_FIELDS.CLIENT_ID,
        minWidth: 260,
      }),
      new ColumnDefinition({
        field: PARTNER_FIELDS.SECRET_LAST_FOUR,
        width: 120,
        valueGetter: ({data}) =>
          data?.hasSecret ? `…${data.secretLastFour ?? ''}` : 'Not set',
      }),
      new ColumnDefinition({
        field: PARTNER_FIELDS.GRANT_COUNT,
        width: 130,
        filter: 'agNumberColumnFilter',
        valueGetter: ({data}) => data?.grantCount ?? 0,
      }),
      new ColumnDefinition({
        field: PARTNER_FIELDS.LAST_USED,
        width: 160,
        filter: 'agDateColumnFilter',
        valueGetter: ({data}) =>
          data?.lastUsedAt ? new Date(data.lastUsedAt).toLocaleString() : 'Never',
      }),
      new ColumnDefinition({
        field: PARTNER_FIELDS.CREATED_AT,
        width: 140,
        filter: 'agDateColumnFilter',
        valueGetter: ({data}) =>
          data?.createdAt ? new Date(data.createdAt).toLocaleDateString() : '',
      }),
    ];
  }, []);

  return (
    <MyPaginatedGrid
      columnDefs={columnDefs}
      contextMenu={getContextMenuItems}
      ls_name={ls_name}
      registerGrid={viewController.setGridToParent}
      registerTempGrid={viewController.setTempGridToParent}
      fetchParams={FETCH_PARAMS}
      onCellDoubleClicked={(params) => props.onOpen(params?.data)}
      onFetchFail={viewController.onFetchFail}
      pagination={props.pagination}
      refreshTimestamp={viewController.refreshTimestamp}
    />
  );
};

PartnersGrid.propTypes = {
  pagination: PropTypes.instanceOf(Object).isRequired,
  viewController: PropTypes.instanceOf(Object).isRequired,
  onOpen: PropTypes.func.isRequired,
};

export default PartnersGrid;
